import { rem } from '@mantine/core';
import { Node, mergeAttributes } from '@tiptap/core';
import getJsonAttribute from 'utils/get-json-attribute';

export const DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY = '__index';

export type DeviceMeasurementsPlaceholderType = 'table' | 'custom';
export type DeviceMeasurementsPlaceholderWidthUnit = 'auto' | 'mm' | '%';

export interface DeviceMeasurementsPlaceholderWidth {
  key: string;
  unit: DeviceMeasurementsPlaceholderWidthUnit;
  value?: number;
}

export interface DeviceMeasurementsPlaceholderColumn {
  key: string;
  label: string;
  type: string;
  show?: boolean;
  suffix?: string;
  precision?: number;
  width?: string;
}

export interface DeviceMeasurementsPlaceholderIndexColumn {
  key: string;
  label: string;
  show?: boolean;
  width?: string;
}

export interface DeviceMeasurementsPlaceholderFormat {
  type: DeviceMeasurementsPlaceholderType;
  widths: DeviceMeasurementsPlaceholderWidth[];
  lineTemplate?: string;
  separator?: string;
  prefix?: string;
  suffix?: string;
  columns?: DeviceMeasurementsPlaceholderColumn[];
  indexColumn?: DeviceMeasurementsPlaceholderIndexColumn;
}

export const DEFAULT_DEVICE_MEASUREMENTS_PLACEHOLDER_FORMAT: DeviceMeasurementsPlaceholderFormat = {
  type: 'table',
  widths: [],
  lineTemplate: '',
  separator: '',
  prefix: '',
  suffix: '',
  columns: [],
  indexColumn: {
    key: DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY,
    label: 'Číslo',
    show: true,
  },
};

/**
 * Pretty prints the selected measurements output format.
 */
function prettyPrintFormat(type: DeviceMeasurementsPlaceholderType) {
  return {
    table: 'Tabuľka',
    custom: 'Vlastný formát - HTML',
  }[type];
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    deviceMeasurementsPlaceholder: {
      insertDeviceMeasurementsPlaceholder: () => ReturnType;
      setDeviceMeasurementsPlaceholderSectionId: (sectionId: string) => ReturnType;
      setDeviceMeasurementsPlaceholderFormat: (format: DeviceMeasurementsPlaceholderFormat) => ReturnType;
      setDeviceMeasurementsPlaceholderConfig: (config: {
        sectionId: string;
        format: DeviceMeasurementsPlaceholderFormat;
      }) => ReturnType;
    };
  }
}

export const DeviceMeasurementsPlaceholder = Node.create({
  name: 'deviceMeasurementsPlaceholder',
  draggable: true,
  inline: false,
  group: 'block',

  addAttributes() {
    return {
      'data-name': { default: 'measurements' },
      'data-format': { default: '' },
      'data-type': { default: 'deviceMeasurements' },
      format: {
        renderHTML: (attributes) => ({ 'data-format': JSON.stringify(attributes.format) }),
        parseHTML: (element) =>
          getJsonAttribute(element, 'data-format', DEFAULT_DEVICE_MEASUREMENTS_PLACEHOLDER_FORMAT),
        default: DEFAULT_DEVICE_MEASUREMENTS_PLACEHOLDER_FORMAT,
      },
      sectionId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-section-id'),
        renderHTML: (attributes) => ({ 'data-section-id': attributes.sectionId }),
      },
      'data-path': {
        renderHTML: (attributes) => ({ 'data-path': `measurements.${attributes.sectionId}` }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div.device-measurements-placeholder', priority: 60 }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const sectionId = node.attrs.sectionId;
    const format = node.attrs.format as DeviceMeasurementsPlaceholderFormat;

    const extendedAttributes = mergeAttributes(HTMLAttributes, {
      class: 'placeholder device-measurements-placeholder',
    });

    const eWrapper = document.createElement('div');
    for (const [key, value] of Object.entries(extendedAttributes)) {
      eWrapper.setAttribute(key, value);
    }
    eWrapper.ondblclick = () => window.dispatchEvent(new CustomEvent('open-device-measurements-configurator'));
    eWrapper.title = 'Dvojklikom otvoríte konfigurátor';
    eWrapper.style.cursor = 'pointer';

    const eText = document.createElement('div');
    eText.textContent = 'Tabuľka meraní ';

    const eFormat = document.createElement('span');

    if (sectionId) {
      eFormat.innerHTML = `(<span data-inject-section-name="${sectionId}"></span> - ${prettyPrintFormat(format.type)})`;
    } else {
      eFormat.textContent = '(Dvojklikom vyberte tabuľku meraní)';
    }

    eFormat.style.color = sectionId ? 'var(--mantine-color-gray-7)' : 'var(--mantine-color-red-7)';
    eFormat.style.fontSize = rem(12);
    eFormat.style.fontWeight = '500';
    eText.appendChild(eFormat);

    eWrapper.appendChild(eText);

    return eWrapper;
  },

  addCommands() {
    return {
      insertDeviceMeasurementsPlaceholder:
        () =>
        ({ commands }) => {
          return commands.insertContent({ type: 'deviceMeasurementsPlaceholder' });
        },
      setDeviceMeasurementsPlaceholderSectionId:
        (sectionId: string) =>
        ({ commands }) => {
          return commands.updateAttributes('deviceMeasurementsPlaceholder', { sectionId });
        },
      setDeviceMeasurementsPlaceholderFormat:
        (format: DeviceMeasurementsPlaceholderFormat) =>
        ({ commands }) => {
          return commands.updateAttributes('deviceMeasurementsPlaceholder', { format });
        },
      setDeviceMeasurementsPlaceholderConfig:
        (config: { sectionId: string; format: DeviceMeasurementsPlaceholderFormat }) =>
        ({ commands }) => {
          return commands.updateAttributes('deviceMeasurementsPlaceholder', config);
        },
    };
  },
});
